import type { ComplianceSummary, Severity } from '@shared/protocol';
import { AlertTriangle, CheckCircle2, HelpCircle, Info, XCircle } from 'lucide-react';

const icon = { PASS: CheckCircle2, INFO: Info, WARNING: AlertTriangle, FAIL: XCircle, UNKNOWN: HelpCircle } as const;
const cls = { PASS: 'pass', INFO: 'info', WARNING: 'warn', FAIL: 'fail', UNKNOWN: 'neutral' } as const;
const word = { PASS: 'PASS', INFO: 'INFO', WARNING: 'WARNING', FAIL: 'FAIL', UNKNOWN: 'NO DATA' } as const;

export function SeverityBadge({ severity, large }: { severity: Severity | 'UNKNOWN'; large?: boolean }) {
  const Icon = icon[severity];
  return (
    <span className={`badge badge-${cls[severity]} ${large ? 'badge-lg' : ''}`}>
      <Icon size={large ? 18 : 14} aria-hidden />
      {word[severity]}
    </span>
  );
}

/** Overall PASS / WARNING / FAIL per selected preset and profile, plus the plain-English summary. */
export function SummaryPanel({ summary, hasTargets }: { summary: ComplianceSummary | null; hasTargets: boolean }) {
  const s = summary;
  return (
    <section className={`card summary-panel summary-${cls[s?.overall ?? 'UNKNOWN']}`} aria-label="Compliance summary">
      <div className="summary-head">
        <div className="summary-overall">
          <span className="muted small">Overall</span>
          <SeverityBadge severity={s?.overall ?? 'UNKNOWN'} large />
        </div>
        <div className="summary-targets">
          {s && s.targets.length > 0 ? (
            s.targets.map((t) => (
              <div key={t.targetId} className="summary-target">
                <span className="summary-target-name">{t.name}</span>
                <SeverityBadge severity={t.status} />
              </div>
            ))
          ) : (
            <span className="muted small">{hasTargets ? 'Waiting for measurements.' : 'No presets or platform profiles selected. Edit the stream to choose what to check against.'}</span>
          )}
        </div>
      </div>
      <p className="summary-paragraph">{s?.paragraph ?? 'Waiting for the first assessment.'}</p>
      {s?.llmParagraph && (
        <div className="summary-llm">
          <span className="muted small">Natural-language version (written by Claude from the findings above; the findings are the source of truth)</span>
          <p>{s.llmParagraph}</p>
        </div>
      )}
    </section>
  );
}
